import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Modal from '@mui/material/Modal';
import cn from 'classnames';

import { RootState, AppDispatch } from 'redux/store';
import { loadMilestones } from 'redux/application/milestones/milestones.middleware';
import { loadApp } from 'redux/application/application.middleware';
import { createMilestones } from 'redux/application/milestones/milestones.middleware';
import { setAlert } from 'redux/alert';
import { getErrorMessage } from 'utils/v2/errorHelper';
import IconBtn from 'components/common/IconBtn';
import LoadingButton from 'components/common/LoadingButton/LoadingButton';
import PrimaryButton from 'components/common/PrimaryButton/PrimaryButton';
import AgreementSignature from 'components/common/AgreementSignature/AgreementSignature';
import PrimaryModal from 'components/common/PrimaryModal/PrimaryModal';
import { ReactComponent as CrossSimpleIcon } from 'assets/icons/cross-simple.svg';
import { ReactComponent as ExclamationPointIcon } from 'assets/icons/exclamation_mark.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';

import api, { MilestonePayload, ApplicationStatus } from 'api';

import styles from './Agreement.module.scss';
import Tip from 'views/ProviderClients/MyClients/components/Tip/Tip';
import UpdateCase from 'views/ProviderClients/MyClients/components/UpdateCase/UpdateCase';
import TotalServiceFee from 'views/ProviderClients/MyClients/views/Agreement/components/TotalServiceFee/TotalServiceFee';
import MilestonesListWithPayment from 'views/ProviderClients/MyClients/views/Agreement/components/MilestonesListWithPayment';
import * as S from '../../index.styled';
import { setActiveApp } from 'redux/application/application.slice';
import { Client } from 'api/models/response/ClientList';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'hooks/useWindowSize';
import { Autocomplete, TextField } from '@mui/material';
import OldMilestonesAndFees from 'views/ProviderClients/MyClients/views/Agreement/components/MilestonesAndFees/OldMilestonesAndFees';

const OldOnboardingAgreement = () => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { id: candidateID, appID } = useParams();
    const [isContractModalOpen, setContractModalOpen] = useState<boolean>(false);
    const [currentMilestones, setCurrentMilestones] = useState<MilestonePayload[]>([]);
    const [isFullscreenModeContractModal, setFullscreenModeContractModal] = useState<boolean>(true);
    const { entity: activeCase } = useSelector((state: RootState) => state.application);

    const milestonesData = useSelector((state: RootState) => state.milestonesApp.list);
    const { newMilestone } = useSelector((state: RootState) => state.milestonesApp);
    const [currentBrowserClient, setCurrentBrowserClient] = useState<Client>();
    const [openCaseProperties, setOpenCaseProperties] = useState<boolean>(false);
    const openCloseCaseProperties = async () => {
        setOpenCaseProperties((prevState) => !prevState);
    };

    const [hasTableErrors, setTableHasErrors] = useState<boolean>(false);
    const [saving, setSaving] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    const [customStyleForFooter, setCustomStyleForFooter] = useState(false);

    const [currency, setCurrency] = useState<string>('USD');

    useEffect(() => {
        //def val
        setCurrency('USD');
        if (milestonesData && milestonesData.length > 0) {
            setCurrency(milestonesData[0].currency);
        }
    }, [appID, milestonesData]);

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    useEffect(() => {
        if (width >= 1120) {
            setCustomStyleForFooter(false);
        }
        if (width < 1120) {
            setCustomStyleForFooter(true);
        }
    }, [width, height]);

    useEffect(() => {
        if (candidateID) {
            getClient(Number(candidateID));
        }
    }, [candidateID]);

    const getClient = async (id: number) => {
        try {
            const resp = await api.clientList.getClientById(id);
            if (resp.status === 200 || resp.status === 201) {
                setCurrentBrowserClient(resp.data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (candidateID && appID) {
            dispatch(loadMilestones(appID.toString()));
        }
    }, [appID, candidateID, dispatch]);

    useEffect(() => {
        if (appID) {
            dispatch(loadApp(appID));
            return;
        }
        dispatch(setActiveApp(null));
        //eslint-disable-next-line
    }, [appID, dispatch]);

    const createOffer = async () => {
        if (!activeCase || !candidateID || saving) {
            return;
        }
        setSaving(true);
        try {
            const updatedCurrMilestoneArr = addCurrencyToMilestones(currentMilestones);
            const payload = {
                appID: activeCase.id.toString(),
                payload: updatedCurrMilestoneArr,
                notifyClient: true,
            };
            await dispatch(createMilestones(payload)).unwrap();
            await dispatch(loadApp(activeCase.id.toString()));
            await dispatch(loadMilestones(activeCase.id.toString()));
            dispatch(
                setAlert({
                    message: t('offerSent', { lng: language }),
                    type: 'success',
                })
            );
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, t('somethingWentWrong', { lng: language })),
                    type: 'error',
                })
            );
        } finally {
            setSaving(false);
        }
    };
    const addCurrencyToMilestones = (currMilestones: MilestonePayload[]) => {
        const tempMilestoneArr = currMilestones.map((item) => ({ ...item, currency: currency }));
        return tempMilestoneArr;
    };

    // ApplicationStatus.OFFER_REJECTED status doesn't have own template for Agreement
    // and for Documents page (Only VisaProgram page is available for this status)
    const getTemplate = () => {
        if (!activeCase) return getDefaultText();
        if (candidateID && appID) {
            if (activeCase.program_name === 'Open Case') {
                return getOpenCaseTemplate();
            }

            if (activeCase.date_agreement_signed) {
                return getSignedAgreementTemplate();
            }
            return getNewRetainerAgreementTemplate();
        }
        return null;
    };

    const getDefaultText = () => {
        return (
            <S.Container>
                <S.Body>
                    <S.selectClientTitle>
                        {t('pleaseSelectCase', { lng: language })}
                    </S.selectClientTitle>
                </S.Body>
            </S.Container>
        );
    };
    const getOpenCaseTemplate = () => {
        return (
            <>
                {activeCase && (
                    <S.TopWrapper>
                        <S.OpenCaseTitle>{t('openCase', { lng: language })}</S.OpenCaseTitle>
                        <PrimaryButton
                            variant="outlined"
                            size="min"
                            color="orange"
                            onClick={openCloseCaseProperties}
                        >
                            <span className={styles.btn}>
                                <PlusIcon className={styles['btn__icon']} />
                                {t('caseProperties', { lng: language })}
                            </span>
                        </PrimaryButton>
                    </S.TopWrapper>
                )}
                <Tip icon={ExclamationPointIcon}>{t('youCantMakeAnOffer', { lng: language })}</Tip>

                <PrimaryModal
                    open={openCaseProperties}
                    onClose={openCloseCaseProperties}
                    maxWidth="640px"
                >
                    {activeCase && <UpdateCase closeModal={openCloseCaseProperties} />}
                </PrimaryModal>
            </>
        );
    };

    const getNewRetainerAgreementTemplate = () => {
        return (
            <>
                {activeCase && (
                    <>
                        <section
                            className={styles.milestones}
                            style={saving ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
                        >
                            <h3 className={styles['milestones__title']}>
                                {activeCase.program_name}
                            </h3>
                            <OldMilestonesAndFees
                                milestones={milestonesData}
                                editMode={!activeCase.date_agreement_signed}
                                isCreating={saving || newMilestone.isLoading}
                                onCurrentMilestonesList={(list) => setCurrentMilestones(list)}
                                onHasErrors={(value: boolean) => setTableHasErrors(value)}
                            />
                        </section>

                        <S.NewRetainerAgreementTemplateFooter
                            mobile={mobile}
                            customStyle={customStyleForFooter}
                        >
                            {activeCase.status !== ApplicationStatus.OFFER && (
                                <Tip
                                    style={customStyleForFooter ? { width: '100%' } : {}}
                                    icon={ExclamationPointIcon}
                                >
                                    {t('youCanAddMilestones', { lng: language })}
                                </Tip>
                            )}
                            {activeCase.status === ApplicationStatus.OFFER && (
                                <Tip
                                    style={customStyleForFooter ? { width: '100%' } : {}}
                                    icon={ExclamationPointIcon}
                                >
                                    {t('yourOfferSentTo', { lng: language })}{' '}
                                    <span className={styles['capital-letters']}>
                                        {currentBrowserClient?.firstname}{' '}
                                        {currentBrowserClient?.lastname}
                                    </span>
                                </Tip>
                            )}
                            <span className="flex items-center gap-2">
                                <TotalServiceFee
                                    style={
                                        mobile
                                            ? { alignSelf: 'flex-start', marginLeft: '0.5rem' }
                                            : customStyleForFooter
                                              ? { alignSelf: 'flex-end', marginRight: '0.5rem' }
                                              : {}
                                    }
                                    milestones={currentMilestones}
                                />
                                <Autocomplete
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: '35px !important',
                                            width: '100px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            '&:focus': {
                                                backgroundColor: '#fff',
                                                borderColor: 'lightgray',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                        },
                                        '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
                                            padding: '0px 4px 7.5px 5px',
                                        },
                                    }}
                                    disableClearable
                                    disablePortal
                                    disabled={saving}
                                    value={currency}
                                    id="currency"
                                    options={['EUR', 'USD', 'GBP', 'CAD', 'AUD']}
                                    onChange={(_, value: string | null) =>
                                        setCurrency(value ?? 'USD')
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={`${t('chooseCurrency', {
                                                lng: language,
                                            })}`}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            </span>
                        </S.NewRetainerAgreementTemplateFooter>

                        {/*previous condition:  {activeCase.status !== ApplicationStatus.OFFER */}
                        {!activeCase.date_agreement_signed && (
                            <div className={styles.actions}>
                                <LoadingButton
                                    isLoading={saving}
                                    disabled={hasTableErrors || currentMilestones.length === 0}
                                    variant="contained"
                                    size="min"
                                    color="orange"
                                    onClick={createOffer}
                                >
                                    {activeCase.status !== ApplicationStatus.OFFER
                                        ? t('saveAndMakeOffer', { lng: language })
                                        : t('save&reissue', { lng: language })}
                                </LoadingButton>
                            </div>
                        )}
                    </>
                )}
            </>
        );
    };

    const openCloseContractModal = () => {
        setContractModalOpen((state) => !state);
    };

    const handleAgreementFullscreenMode = (value: boolean) => {
        setFullscreenModeContractModal(value);
    };

    // Styles
    const agreementModalWrapperStyle = cn(styles['modal-contract-wrapper'], {
        [styles['modal-contract-wrapper_fullscreen']]: isFullscreenModeContractModal,
    });

    const agreementModalStyle = cn(styles['modal-contract'], {
        [styles['modal-contract_border-none']]: isFullscreenModeContractModal,
    });

    const getSignedAgreementTemplate = () => {
        return (
            <>
                {activeCase && (
                    <>
                        <section className={styles.milestones}>
                            <h3 className={styles['milestones__title']}>
                                {activeCase.program_name}
                            </h3>
                            <MilestonesListWithPayment
                                milestones={milestonesData}
                                isAgreementSigned={true}
                            />
                        </section>
                        <S.SignedRetainerAgreementTemplateFooter>
                            <TotalServiceFee milestones={milestonesData} />
                        </S.SignedRetainerAgreementTemplateFooter>
                    </>
                )}

                <Modal
                    sx={{ zIndex: '10000' }}
                    open={isContractModalOpen}
                    onClose={openCloseContractModal}
                >
                    <div className={agreementModalWrapperStyle}>
                        <div className={agreementModalStyle}>
                            <div className={styles['modal-contract__btn-wrapper']}>
                                <IconBtn
                                    style={{ height: '24px', width: '24px' }}
                                    onClick={openCloseContractModal}
                                >
                                    <CrossSimpleIcon className={styles['close-icon']} />
                                </IconBtn>
                            </div>
                            <AgreementSignature
                                appID={activeCase ? activeCase.id.toString() : null}
                                onFullscreenMode={handleAgreementFullscreenMode}
                                onUserSignedAgreement={() => {
                                    console.log('adf');
                                }}
                            />
                        </div>
                    </div>
                </Modal>
            </>
        );
    };

    // const getCandidateCounterOfferTemplate = () => {
    //     return (
    //         <>
    //             {activeCase && (
    //                 <>
    //                     {/* <section className={styles['top-wrapper']}>
    //                         <h1 className={styles.title}>Retainer Agreement</h1>
    //                         <Status color={StatusColor.processed} name="Negotiation" />
    //                     </section> */}
    //                     <h3 className={styles['milestones__title']}>{activeCase.program_name}</h3>
    //                     <Outlet />
    //                 </>
    //             )}
    //         </>
    //     );
    // };

    // const getConsultantCounterOfferTemplate = () => {
    //     return (
    //         <>
    //             {activeCase && (
    //                 <>
    //                     {/* <section className={styles['top-wrapper']}>
    //                         <h1 className={styles.title}>Retainer Agreement</h1>
    //                         <Status color={StatusColor.processed} name="Negotiation" />
    //                     </section> */}

    //                     <section className={styles.milestones}>
    //                         <h3 className={styles['milestones__title']}>
    //                             {activeCase.program_name}
    //                         </h3>
    //                         <MilestonesAndFees
    //                             milestones={milestonesData}
    //                             editMode={false}
    //                             isCreating={newMilestone.isLoading}
    //                             onCurrentMilestonesList={(list) => setCurrentMilestones(list)}
    //                         />
    //                     </section>

    //                     <Tip icon={ExclamationPointIcon}>
    //                         {`Your counteroffer has been sent to the ${currentClient?.firstname} ${currentClient?.lastname}`}
    //                     </Tip>

    //                     <Tip icon={HourglassIcon} mt="14px">
    //                         Waiting for a decision by{' '}
    //                         <span className={styles['capital-letters']}>
    //                             {currentClient?.firstname} {currentClient?.lastname}
    //                         </span>
    //                     </Tip>
    //                 </>
    //             )}
    //         </>
    //     );
    // };

    // const getOfferAcceptedTemplate = () => {
    //     return (
    //         <>
    //             {activeCase && (
    //                 <>
    //                     {/* <section className={styles['top-wrapper']}>
    //                         <h1 className={styles.title}>Retainer Agreement</h1>
    //                         <Status color={StatusColor.processed} name="Negotiation" />
    //                     </section> */}

    //                     <section className={styles.milestones}>
    //                         <h3 className={styles['milestones__title']}>
    //                             {activeCase.program_name}
    //                         </h3>
    //                         <MilestonesAndFees
    //                             milestones={milestonesData}
    //                             editMode={false}
    //                             isCreating={newMilestone.isLoading}
    //                             onCurrentMilestonesList={(list) => setCurrentMilestones(list)}
    //                         />
    //                     </section>

    //                     <Tip icon={ExclamationPointIcon}>
    //                         You accepted{' '}
    //                         <span className={styles['capital-letters']}>
    //                             {currentClient?.firstname} {currentClient?.lastname}
    //                         </span>
    //                         's counter offer
    //                     </Tip>

    //                     <Tip icon={HourglassIcon} mt="14px">
    //                         Waiting for the contract to be signed by{' '}
    //                         <span className={styles['capital-letters']}>
    //                             {currentClient?.firstname} {currentClient?.lastname}
    //                         </span>
    //                     </Tip>
    //                 </>
    //             )}
    //         </>
    //     );
    // };

    return <>{getTemplate()}</>;
};

export default OldOnboardingAgreement;
